import {
  changePassword,
  changePhone,
  createActionVerification,
  createEvoClient,
  generateWalletPass,
  getProfile,
  initAuthentication,
  initRegistration,
  logout,
  requestPromoCode,
  updateEmailRequest,
  updateProfile,
  verifyAge,
  verifyPhoneOrEmail,
} from "@jti/evo-client";
import type {
  CasAddressStored,
  ContactPersonal,
  DateOfBirth,
  Gender,
  LoginAuthenticationStrategies,
  OtpChannel,
  PreparedProfileType,
  ResponseProfileType,
} from "@jti/fe-common";
import {
  DOB_INPUT_DATE,
  getConfig,
  ISO_DATE_FORMAT,
  LOCAL_USER_UPDATED_EVENT,
  parseCustomDate,
  PHONE_AUTH_STRATEGY,
} from "@jti/fe-common";
import type { WalletSuccessResponse, WalletType } from "@jti/rest-api-types";

import { BrowserStorage, getDocumentLocale, getUser, User } from "../../../utils";
import type { RestResponseType } from "../../Rest";
import { convertEvoAddressToCasAddressStored, handleAddPlusToPhone, prepareUserProfileEvo } from "../utils";

import type { CasProfileApiContract } from "./CasProfileApi.types";

export class CasProfileApi implements CasProfileApiContract {
  private readonly client;

  constructor(
    baseUrl: string,
    protected tenantToken: string,
    protected recaptchaId: string,
    protected ciamToken: string,
    protected userUuid: string
  ) {
    this.client = createEvoClient({
      baseUrl,
      throwOnError: true,
      tenantToken,
      recaptchaId,
      ciamToken,
    });
  }

  static init() {
    const { ciam_token, uuid } = getUser();
    const { EVO_DOMAIN, EVO_TENANT, "X-RECAPTCHA-ID": recaptchaId } = getConfig();

    return new CasProfileApi(EVO_DOMAIN, EVO_TENANT, recaptchaId, ciam_token, uuid || "");
  }

  async getProfile(): Promise<PreparedProfileType> {
    const { data: responseData } = await getProfile({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
    });

    const { data, meta } = responseData || {};

    if (!data?.consumer) {
      throw new Error("EVO profile response missing consumer");
    }

    const consumer = data.consumer;

    new User(new BrowserStorage()).setUser({
      ...consumer,
      is_age_verified: meta?.is_age_verified,
      // Remove this once EVO exposes it.
      ageVerificationExpirationDate: meta?.two_factor_authentication_status?.expires_at ?? null,
      referrals: { ...data.referrals },
      phone: consumer.phone ?? undefined,
      parent_code: consumer.parent_code ?? undefined,
      created_at: consumer.created_at ?? undefined,
    });

    window.dispatchEvent(new Event(LOCAL_USER_UPDATED_EVENT));

    const preparedData = prepareUserProfileEvo(data, meta);
    return preparedData;
  }

  async logout(): Promise<boolean> {
    await logout({
      client: this.client,
    });
    return true;
  }

  async changePassword(
    _uuid: string,
    _email: string,
    newPassword: string,
    currentPassword: string,
    confirmPassword: string
  ): Promise<boolean> {
    await changePassword({
      client: this.client,
      body: {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      },
    });
    return true;
  }

  async updateEmail(email: string) {
    const { response } = await updateEmailRequest({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
      body: {
        email,
      },
    });

    return { statusCode: response.status };
  }

  async updatePhone(
    phone: string,
    pvs_uuid?: string,
    avp_uuid?: string
  ): Promise<{ personalInfo: ContactPersonal; addresses: CasAddressStored[] }> {
    const { data } = await changePhone({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
      body: {
        phone: handleAddPlusToPhone(phone),
        action_verification_uuid: pvs_uuid,
        age_verification_uuid: avp_uuid,
      },
    });

    return {
      personalInfo: {
        firstname: data?.first_name || "",
        lastname: data?.last_name || "",
        dob: data?.date_of_birth || "",
        email: data?.email || "",
        gender: data?.gender,
        telephone: data?.phone || "",
      },
      addresses: data?.addresses?.map(convertEvoAddressToCasAddressStored) ?? [],
    };
  }

  async updateAgeVerification(avp_uuid: string): Promise<{ is_age_verified: boolean }> {
    const { data } = await verifyAge({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
      body: {
        age_verification_uuid: avp_uuid,
      },
    });
    return { is_age_verified: data?.is_age_verified || false };
  }

  async sendOTC(phone: string, otpChannel: OtpChannel = "sms", otpTemplate?: string) {
    const { data } = await createActionVerification({
      client: this.client,
      body: {
        channel: otpChannel,
        identifier: handleAddPlusToPhone(phone),
        template: otpTemplate,
      },
    });

    if (!data) {
      throw new Error("No data received from the server");
    }

    return {
      phone_verification_uuid: data.data?.uuid,
    };
  }

  async sendLoginOTC(
    phone: string,
    otpChannel: OtpChannel = "sms",
    otpTemplate?: string,
    strategy: LoginAuthenticationStrategies = PHONE_AUTH_STRATEGY
  ) {
    const { error, response, data } = await initAuthentication({
      client: this.client,
      body: {
        strategy,
        strategy_data: {
          channel: otpChannel || "sms",
          identifier: handleAddPlusToPhone(phone),
          template: otpTemplate,
        },
      },
      throwOnError: false,
    });

    if (error) {
      throw { ...error, statusCode: String(response.status) };
    }

    return {
      phone_verification_uuid: data?.data?.uuid ?? "",
    };
  }

  async sendEmailOTC(email: string) {
    const locale = getDocumentLocale();
    const { data } = await initRegistration({
      client: this.client,
      body: {
        channel: "email",
        identifier: email,
      },
      ...(locale && { headers: { "x-des-locale": locale } }),
    });
    return { email_verification_uuid: data?.data?.uuid ?? "" };
  }

  async verifiedPhone(phone: string): Promise<RestResponseType> {
    const { response, error } = await verifyPhoneOrEmail({
      client: this.client,
      path: {
        phone_or_email: handleAddPlusToPhone(phone),
      },
      throwOnError: false,
    });

    if (error) {
      throw { ...error, statusCode: response?.status };
    }

    return { statusCode: response.status };
  }

  async updatePersonalData(
    firstname: string,
    lastname: string,
    date_of_birth: DateOfBirth,
    gender?: Gender,
    dobFormat = DOB_INPUT_DATE
  ): Promise<ResponseProfileType> {
    const { data: responseData } = await updateProfile({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
      body: {
        first_name: firstname,
        last_name: lastname,
        date_of_birth: date_of_birth
          ? parseCustomDate({
              date: date_of_birth,
              initialFormat: dobFormat,
              resultFormat: ISO_DATE_FORMAT,
            })
          : "",
        gender: gender,
      },
    });

    const { data, meta } = responseData || {};

    return {
      is_age_verified: meta?.is_age_verified || false,
      is_pending_email_verification: data?.is_pending_email_verification || false,
      personal_info: {
        email: data?.consumer?.email || "",
        firstname: data?.consumer?.first_name || "",
        lastname: data?.consumer?.last_name || "",
        date_of_birth: data?.consumer?.date_of_birth || "",
        phone: data?.consumer?.phone || "",
        telephone: data?.consumer?.phone || "",
        gender: data?.consumer?.gender || "prefer_not_to_say",
      },
      extra: {},
      purposes: data?.purposes || [],
      meta: {
        allow_date_of_birth_update: meta?.allow_date_of_birth_update ?? false,
        allow_name_update: meta?.allow_name_update,
      },
      addresses: data?.consumer?.addresses?.map(convertEvoAddressToCasAddressStored) ?? [],
    };
  }

  async requestDiscount() {
    const { response } = await requestPromoCode({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
    });
    return { statusCode: response.status };
  }

  async requestWalletUrl(type: WalletType): Promise<WalletSuccessResponse> {
    const { data } = await generateWalletPass({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
      body: { type },
    });

    const walletData = data?.data;

    if (!walletData) {
      throw new Error("No data received from wallet URL request");
    }

    return {
      data: {
        wallet_type: walletData.wallet_type ?? "",
        wallet_object_id: walletData.wallet_object_id ?? "",
        pass_url: walletData.pass_url ?? "",
      },
    };
  }
}
